import { normalizeTextInput } from "@/utils/zodString";
import { slugifyCatalogSegment } from "@/utils/catalogSlug";
import { slugifyClassName } from "../utils/class.utils";
import type { ClassFormSchemaValues } from "../schemas/classForm.schema";
import type {
  AdminClass,
  ClassBenefit,
  ClassLevels,
} from "../types/class.types";
import type {
  AdminClassApiItem,
  AdminSchoolDetailsApi,
  CreateClassPayload,
  UpdateClassPayload,
} from "./adminClass.types";

function levelByTier(
  levels: AdminClassApiItem["levels"],
  tier: number
): AdminClassApiItem["levels"][number] | undefined {
  return levels.find((l) => l.tier === tier);
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isValidUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}

function mapSchoolCourseIds(
  course: AdminSchoolDetailsApi["course"] | undefined
): string[] {
  return (course ?? [])
    .map((item) => (typeof item === "string" ? item : item?.id ?? ""))
    .map((id) => id.trim())
    .filter((id) => isValidUuid(id));
}

export function mapApiItemToAdminClass(item: AdminClassApiItem): AdminClass {
  const l1 = levelByTier(item.levels, 1);
  const l2 = levelByTier(item.levels, 2);
  const l3 = levelByTier(item.levels, 3);
  const school = item.school_details;

  const levels: ClassLevels = {
    level1Id: l1?.level_id ?? "",
    level1Label: l1?.level_name ?? "",
    level2Id: l2?.level_id ?? "",
    level2Label: l2?.level_name ?? "",
    level3Id: l3?.level_id ?? "",
    level3Label: l3?.level_name ?? "",
  };

  const benefits: ClassBenefit[] = (item.benefits ?? []).map((b) => ({
    id: b.id,
    title: b.title,
    description: b.description,
  }));

  const schoolCourseIds = mapSchoolCourseIds(school?.course);
  const linkedCourseId = item.linked_course?.id?.trim();
  const courseId =
    (item.course_id?.trim() && isValidUuid(item.course_id.trim())
      ? item.course_id.trim()
      : undefined) ??
    (linkedCourseId && isValidUuid(linkedCourseId)
      ? linkedCourseId
      : undefined) ??
    schoolCourseIds[0] ??
    "";

  return {
    id: item.id,
    slug: slugifyClassName(item.class_name),
    categorySlug:
      slugifyCatalogSegment(l2?.level_name ?? "") ||
      slugifyCatalogSegment(l1?.level_name ?? ""),
    name: item.class_name,
    description: item.description,
    validityDays: item.validity_in_days,
    price: parseFloat(item.price) || 0,
    taxPercent: parseFloat(item.tax_percentage) || 0,
    totalPrice: parseFloat(item.total_price) || 0,
    levels,
    benefits,
    school: {
      schoolName: school?.name ?? "",
      adminName: school?.admin_name ?? "",
      email: school?.email ?? "",
      contactNumber: school?.contact_number ?? "",
      linkedGradeIds: courseId ? [courseId] : schoolCourseIds,
      schoolTitle: school?.title,
      licenseCountTeacher: school?.license_count_teacher,
      licenseCountStudent: school?.license_count_student,
      location: school?.location,
    },
    thumbnailUrl: item.thumbnail_upload?.preview_url,
    thumbnailCropUrl:
      item.thumbnail_upload?.crop_url ?? item.thumbnail_upload?.preview_url,
    uploadId: item.thumbnail_upload?.id,
    schoolId: school?.id ?? item.school_id,
    courseId,
    courseName: item.linked_course?.grade,
    studentsOnboarded: item.students_count ?? 0,
    isActive: item.is_active,
    createdAt: item.created_on,
    updatedAt: item.created_on,
  };
}

export function adminClassToFormValues(record: AdminClass): ClassFormSchemaValues {
  return {
    name: normalizeTextInput(record.name),
    description: normalizeTextInput(record.description),
    validityDays: record.validityDays,
    price: record.price,
    taxPercent: record.taxPercent,
    level1Id: record.levels.level1Id,
    level2Id: record.levels.level2Id,
    level3Id: record.levels.level3Id,
    benefits: record.benefits.map((b) => ({
      ...b,
      title: normalizeTextInput(b.title),
      description: normalizeTextInput(b.description),
    })),
    schoolName: normalizeTextInput(record.school.schoolName),
    adminName: normalizeTextInput(record.school.adminName),
    email: normalizeTextInput(record.school.email),
    contactNumber: normalizeTextInput(record.school.contactNumber),
    courseId: record.courseId ?? record.school.linkedGradeIds[0] ?? "",
    thumbnailUrl: record.thumbnailUrl ?? "",
    uploadId: record.uploadId ?? "",
    schoolId: record.schoolId ?? "",
    schoolTitle: normalizeTextInput(record.school.schoolTitle ?? "Admin"),
    licenseCountTeacher: record.school.licenseCountTeacher ?? 0,
    licenseCountStudent: record.school.licenseCountStudent ?? 0,
    location: record.school.location ?? {
      address: "",
      district: "",
      city: "",
      state: "",
      pincode: "",
    },
  };
}

function formSchoolAssignment(
  values: ClassFormSchemaValues
): Pick<CreateClassPayload, "school_id" | "course_id"> {
  const result: Pick<CreateClassPayload, "school_id" | "course_id"> = {};
  const schoolId = values.schoolId?.trim();
  if (schoolId && isValidUuid(schoolId)) {
    result.school_id = schoolId;
  }
  const courseId = values.courseId?.trim();
  if (courseId && isValidUuid(courseId)) {
    result.course_id = courseId;
  }
  return result;
}

export function formToCreatePayload(values: ClassFormSchemaValues): CreateClassPayload {
  const base: CreateClassPayload = {
    class_name: values.name.trim(),
    description: values.description.trim(),
    validity_in_days: Number(values.validityDays),
    price: String(values.price),
    tax_percentage: String(values.taxPercent),
    level_1_id: values.level1Id,
    level_2_id: values.level2Id,
    level_3_id: values.level3Id,
    upload_id: values.uploadId,
    benefits: values.benefits.map((b) => ({
      title: b.title.trim(),
      description: b.description.trim(),
    })),
  };

  return { ...base, ...formSchoolAssignment(values) };
}

export function formToUpdatePayload(
  values: ClassFormSchemaValues,
  isActive: boolean
): UpdateClassPayload {
  return {
    class_name: values.name.trim(),
    description: values.description.trim(),
    validity_in_days: Number(values.validityDays),
    price: String(values.price),
    tax_percentage: String(values.taxPercent),
    level_1_id: values.level1Id,
    level_2_id: values.level2Id,
    level_3_id: values.level3Id,
    upload_id: values.uploadId,
    benefits: values.benefits.map((b) => ({
      title: b.title.trim(),
      description: b.description.trim(),
    })),
    is_active: isActive,
    ...formSchoolAssignment(values),
  };
}
